/**
 * pages/Equipment/EquipmentUsageForm.tsx - 设备使用记录表单组件
 *
 * 功能说明：
 * - 设备使用记录创建/编辑表单
 * - 支持自动价格计算
 * - 设备提供方和经手人搜索
 *
 * 依赖关系：
 * - 依赖 antd 组件
 * - 依赖 @tanstack/react-query
 * - 依赖 services
 *
 * 关键注释索引：
 * - [表单提交] - 见下方 handleSubmit
 * - [价格自动计算] - 见下方 handleUsageHoursChange
 *
 * 创建日期：2026-05-03
 * 作者：AI Assistant
 * 重构日期：2026-05-03 - 添加详细中文注释
 */

import React, { useEffect, useState } from 'react';
import {
  Select,
  DatePicker,
  message,
  InputNumber,
  Input,
  AutoComplete,
} from 'antd';
import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EquipmentUsageService } from '../../services/equipmentUsageService';
import { ProjectService } from '../../services/projectService';
import { PresetDataService } from '../../services/presetDataService';
import { equipmentPriceService } from '../../services/equipmentPriceService';
import MobileForm from '../../responsive/components/MobileForm';
import { listSuppliers, getProviderEquipmentNames } from '../../services/supplierService';
import {
  EquipmentUsage,
  CreateEquipmentUsageData,
  UpdateEquipmentUsageData,
} from '../../types';
import {
  calculateEquipmentTotal3,
  formatEquipmentMoney3,
  hasEquipmentMoreThan3Decimals,
} from '../../utils/equipmentPrecision';

const { Option } = Select;

interface EquipmentUsageFormProps {
  initialValues?: EquipmentUsage | null;
  defaultProjectId?: string;
  onSuccess: () => void;
  onCancel: () => void;
}

/**
 * [设备使用表单] 设备使用记录创建/编辑表单组件
 *
 * @description 支持功能：
 * - 项目选择
 * - 设备提供方和设备名称搜索
 * - 自动价格计算
 * - 经手人自动补全
 */
const EquipmentUsageForm: React.FC<EquipmentUsageFormProps> = ({
  initialValues,
  defaultProjectId,
  onSuccess,
  onCancel,
}) => {
  // ==================== 状态定义 ====================

  /** 选中的设备名称 */
  const [selectedEquipmentName, setSelectedEquipmentName] = useState<string>('');
  /** 表单实例 */
  const [formInstance, setFormInstance] = useState<any>(null);
  /** 经手人搜索关键词 */
  const [handlerSearchText, setHandlerSearchText] = useState<string>('');
  /** 供应商搜索关键词 */
  const [providerSearchText, setProviderSearchText] = useState<string>('');
  /** 选中的供应商ID */
  const [selectedProviderId, setSelectedProviderId] = useState<string | undefined>(
    initialValues?.equipmentProviderId
  );
  /** 供应商关联的设备名称列表 */
  const [providerEquipmentNames, setProviderEquipmentNames] = useState<string[]>([]);

  /** 是否为编辑模式 */
  const isEditing = !!initialValues;

  // ==================== 数据查询 ====================

  /** 获取活跃项目列表 */
  const { data: projectsData } = useQuery({
    queryKey: ['projects', 'active'],
    queryFn: () => ProjectService.getActiveProjects(),
  });

  /** 获取预设设备列表 */
  const { data: presetEquipmentData } = useQuery({
    queryKey: ['preset-equipment', 'all'],
    queryFn: () => PresetDataService.getEquipment({ limit: 1000 }),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  /**
   * [设备价格查询] 根据选中的设备名称获取价格配置
   *
   * @description 自动计算设备使用成本
   */
  const { data: equipmentPriceData } = useQuery({
    queryKey: ['equipment-price', selectedEquipmentName],
    queryFn: async () => {
      if (!selectedEquipmentName) return null;
      try {
        const response = await equipmentPriceService.getEquipmentPrices({
          equipmentName: selectedEquipmentName,
        });
        const prices = (response?.data || []).filter(
          (p: any) =>
            p.equipmentName === selectedEquipmentName ||
            p.equipmentName?.includes(selectedEquipmentName)
        );

        if (prices.length !== 1) {
          return null;
        }

        return equipmentPriceService.resolveUsageRate(prices[0]);
      } catch (error) {
        console.error('获取设备价格失败:', error);
        return null;
      }
    },
    enabled: !!selectedEquipmentName,
  });

  /** 搜索工人列表（经手人自动补全） */
  const { data: workerSearchData } = useQuery({
    queryKey: ['workers-search-handler', handlerSearchText],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '100' });
      const keyword = handlerSearchText.trim();
      if (keyword) params.set('search', keyword);

      const stored = localStorage.getItem('auth-storage');
      const token = stored ? JSON.parse(stored)?.state?.token : null;
      const res = await fetch(`/api/worker-module/workers?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (res.ok) {
        const d = await res.json();
        const list = d.data?.workers || d.data || [];
        return list
          .filter((w: any) => w.workerType === 'manager')
          .map((w: any) => ({ value: w.name, label: w.name }));
      }
      return [];
    },
    staleTime: 60 * 1000,
  });

  /** 搜索供应商列表（设备提供方自动补全） */
  const { data: providerSearchData } = useQuery({
    queryKey: ['suppliers-search-provider', providerSearchText],
    queryFn: async () => {
      const res = await listSuppliers(providerSearchText || undefined, 20, 'equipment');
      return (res.data || []).map((s: any) => ({
        value: s.name,
        label: s.name,
        supplierId: s._id,
      }));
    },
    staleTime: 60 * 1000,
  });

  // ==================== 数据提交 ====================

  /** 创建设备使用记录 */
  const createMutation = useMutation({
    mutationFn: (data: CreateEquipmentUsageData) =>
      EquipmentUsageService.createEquipmentUsage(data),
    onSuccess: () => {
      message.success('设备使用记录创建成功');
      onSuccess();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || '创建失败');
    },
  });

  /** 更新设备使用记录 */
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateEquipmentUsageData }) =>
      EquipmentUsageService.updateEquipmentUsage(id, data),
    onSuccess: () => {
      message.success('设备使用记录更新成功');
      onSuccess();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || '更新失败');
    },
  });

  // ==================== 表单处理 ====================

  /** 获取表单初始值 */
  const getInitialValues = () => {
    if (initialValues) {
      return {
        projectId: initialValues.projectId,
        equipmentName: initialValues.equipmentName,
        usageDate: dayjs(initialValues.usageDate),
        usageHours: initialValues.usageHours,
        cost: initialValues.cost,
        remarks: initialValues.remarks,
        constructionType: initialValues.constructionType || null,
        handledByName: initialValues.handledByName || '',
        equipmentProviderId: initialValues.equipmentProviderId || undefined,
        equipmentProviderName: initialValues.equipmentProviderName || '',
      };
    } else {
      return {
        projectId: defaultProjectId,
        usageDate: dayjs(),
      };
    }
  };

  /**
   * [供应商变化] 处理设备提供方变化
   *
   * @description 同步供应商ID和名称，清空设备名称
   */
  const handleProviderChange = (value?: string) => {
    const nextValue = value || '';
    const matchedProvider = (providerSearchData || []).find(
      (item: any) => item.value === nextValue
    );
    const nextProviderId = matchedProvider?.supplierId;

    formInstance?.setFieldValue('equipmentProviderName', nextValue || undefined);
    formInstance?.setFieldValue('equipmentProviderId', nextProviderId);

    if (nextProviderId !== selectedProviderId) {
      formInstance?.setFieldValue('equipmentName', undefined);
      setSelectedEquipmentName('');
    }

    setSelectedProviderId(nextProviderId);
  };

  /**
   * [表单提交] 处理表单提交
   */
  const handleSubmit = async (values: any) => {
    if (hasEquipmentMoreThan3Decimals(values.usageHours) || hasEquipmentMoreThan3Decimals(values.cost)) {
      message.info('设备使用时间或成本最多保留 3 位小数，已按截断规则提交');
    }

    const formData = {
      projectId: values.projectId,
      equipmentName: values.equipmentName,
      usageDate: values.usageDate.format('YYYY-MM-DD'),
      usageHours: values.usageHours,
      cost: values.cost,
      remarks: values.remarks,
      constructionType: values.constructionType || null,
      handledByName: values.handledByName || undefined,
      equipmentProviderId: values.equipmentProviderId || selectedProviderId || undefined,
      equipmentProviderName: values.equipmentProviderName || undefined,
    };

    if (isEditing && initialValues) {
      updateMutation.mutate({
        id: initialValues._id,
        data: formData,
      });
    } else {
      createMutation.mutate(formData);
    }
  };

  // ==================== 设备名称处理 ====================

  /** 设备名称下拉选项 */
  const equipmentNameOptions = React.useMemo(() => {
    const presetEquipments = (presetEquipmentData as any)?.data || [];
    const providerNames =
      providerEquipmentNames.length > 0
        ? providerEquipmentNames
        : Array.from(
            new Set(
              presetEquipments
                .map((item: any) => item.equipmentName)
                .filter(Boolean)
            )
          );
    const keyword = selectedEquipmentName.trim().toLowerCase();
    const matched = (providerNames as string[])
      .filter((name: string) => name !== '其它')
      .filter((name: string) => !keyword || name.toLowerCase().includes(keyword))
      .slice(0, 100)
      .map((name: string) => ({ value: name, label: name }));
    return [{ value: '其它', label: '其它' }, ...matched];
  }, [presetEquipmentData, providerEquipmentNames, selectedEquipmentName]);

  /** 设备名称搜索 */
  const handleEquipmentNameSearch = (value?: string) => {
    setSelectedEquipmentName(value || '');
  };

  /** 设备名称变化 */
  const handleEquipmentNameChange = (value?: string) => {
    formInstance?.setFieldValue('equipmentName', value || undefined);
    setSelectedEquipmentName(value || '');
  };

  /**
   * [使用时长变化] 当使用时长变化时，重新计算成本
   *
   * @description 根据设备价格配置自动计算成本
   */
  const handleUsageHoursChange = (value: number | null) => {
    if (equipmentPriceData && value !== null && value !== undefined && value > 0) {
      const calculatedCost = calculateEquipmentTotal3(value, equipmentPriceData.hourlyPrice);
      formInstance?.setFieldValue('cost', calculatedCost);
    }
  };

  // ==================== 副作用处理 ====================

  /** 当价格数据变化时，自动计算成本 */
  useEffect(() => {
    if (equipmentPriceData && formInstance) {
      const usageHours = formInstance.getFieldValue('usageHours');
      if (usageHours !== null && usageHours !== undefined && usageHours > 0) {
        const calculatedCost = calculateEquipmentTotal3(usageHours, equipmentPriceData.hourlyPrice);
        formInstance.setFieldValue('cost', calculatedCost);
      }
    }
  }, [equipmentPriceData, formInstance]);

  /** 初始化设备名称 */
  useEffect(() => {
    setSelectedEquipmentName(initialValues?.equipmentName || '');
  }, [initialValues?.equipmentName]);

  /** 初始化供应商ID */
  useEffect(() => {
    if (initialValues?.equipmentProviderId) {
      setSelectedProviderId(initialValues.equipmentProviderId);
    }
  }, [initialValues?.equipmentProviderId]);

  /** 加载供应商关联的设备名称 */
  useEffect(() => {
    const loadProviderEquipmentNames = async () => {
      if (!selectedProviderId) {
        setProviderEquipmentNames([]);
        return;
      }

      try {
        const response = await getProviderEquipmentNames(selectedProviderId);
        setProviderEquipmentNames(response.data || []);
      } catch (error) {
        console.error('获取设备提供方关联设备失败:', error);
        setProviderEquipmentNames([]);
      }
    };

    loadProviderEquipmentNames();
  }, [selectedProviderId]);

  // ==================== 表单渲染 ====================

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <MobileForm
        initialValues={getInitialValues()}
        onFinish={handleSubmit}
        disabled={isLoading}
        submitText={isEditing ? '更新' : '创建'}
        onCancel={onCancel}
        submitLoading={isLoading}
        onFormInstanceReady={setFormInstance}
        fields={[
          {
            name: 'projectId',
            label: '项目',
            rules: [{ required: true, message: '请选择项目' }],
            children: (
              <Select
                placeholder="请选择项目"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children?.toString().toLowerCase().includes(input.toLowerCase()) || false
                }
              >
                {projectsData?.data?.map((project) => (
                  <Option key={project._id} value={project._id}>
                    {project.name}
                  </Option>
                ))}
              </Select>
            ),
          },
          {
            name: 'equipmentProviderId',
            label: '设备提供方ID',
            children: <Input type="hidden" />,
            hidden: true,
          },
          {
            name: 'equipmentProviderName',
            label: '设备提供方',
            children: (
              <AutoComplete
                options={providerSearchData || []}
                placeholder="请输入或搜索设备提供方名称"
                onSearch={setProviderSearchText}
                onChange={handleProviderChange}
                filterOption={false}
                allowClear
              />
            ),
          },
          {
            name: 'equipmentName',
            label: '设备名称',
            rules: [
              { required: true, message: '请输入设备名称' },
              { max: 100, message: '设备名称不能超过100个字符' },
            ],
            children: (
              <AutoComplete
                options={equipmentNameOptions}
                placeholder={
                  selectedProviderId
                    ? '请输入或选择该提供方关联设备名称'
                    : '请输入或选择设备名称'
                }
                allowClear
                onSearch={handleEquipmentNameSearch}
                onChange={handleEquipmentNameChange}
                filterOption={false}
              />
            ),
          },
          {
            name: 'usageDate',
            label: '使用日期',
            rules: [{ required: true, message: '请选择使用日期' }],
            children: (
              <DatePicker
                style={{ width: '100%' }}
                format="YYYY-MM-DD"
                placeholder="请选择使用日期"
              />
            ),
          },
          {
            name: 'usageHours',
            label: '使用时长（小时）',
            rules: [
              {
                validator: async (_: any, value: any) => {
                  if (value === '' || value === null || value === undefined) {
                    return;
                  }
                  if (Number(value) < 0) {
                    throw new Error('使用时长不能为负数');
                  }
                },
              },
            ],
            children: (
              <InputNumber
                style={{ width: '100%' }}
                placeholder="请输入使用时长"
                min={0}
                max={24}
                step={0.001}
                precision={3}
                onChange={handleUsageHoursChange}
              />
            ),
          },
          {
            name: 'cost',
            label: (
              <span>
                设备成本（元）
                {equipmentPriceData && (
                  <span style={{ fontSize: 12, color: '#52c41a', marginLeft: 8 }}>
                    已关联{equipmentPriceData.priceTypeLabel} (¥{equipmentPriceData.price}/
                    {equipmentPriceData.label}，按小时折算 {formatEquipmentMoney3(equipmentPriceData.hourlyPrice)}/小时)
                  </span>
                )}
              </span>
            ),
            rules: [
              {
                type: 'number',
                min: 0,
                message: '设备成本不能为负数',
                transform: (value: any) => {
                  if (value === '' || value === null || value === undefined) {
                    return undefined;
                  }
                  return Number(value);
                },
              },
            ],
            children: (
              <InputNumber
                style={{ width: '100%' }}
                placeholder={
                  equipmentPriceData
                    ? '系统将按关联价格自动计算'
                    : '请输入设备成本（可选）'
                }
                min={0}
                step={0.001}
                precision={3}
                formatter={(value) => `¥ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => value!.replace(/¥\s?|(,*)/g, '') as any}
              />
            ),
          },
          {
            name: 'remarks',
            label: '备注说明',
            rules: [{ max: 500, message: '备注不能超过500个字符' }],
            children: (
              <Input.TextArea
                placeholder="请输入使用说明或备注信息（可选）"
                rows={3}
                maxLength={500}
                showCount
              />
            ),
          },
          {
            name: 'constructionType',
            label: '施工类型',
            children: (
              <AutoComplete
                placeholder="请输入或选择施工类型"
                allowClear
                options={[
                  { value: '其它' },
                  { value: '园林' },
                  { value: '土建' },
                  { value: '外墙' },
                ]}
                filterOption={(inputValue, option) =>
                  String(option?.value ?? '')
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                }
              />
            ),
          },
          {
            name: 'handledByName',
            label: '经手人',
            rules: [{ max: 50, message: '经手人姓名不能超过50个字符' }],
            children: (
              <AutoComplete
                options={workerSearchData || []}
                placeholder="请输入或搜索经手人姓名"
                onSearch={setHandlerSearchText}
                filterOption={false}
                allowClear
              />
            ),
          },
        ]}
      />
    </>
  );
};

export default EquipmentUsageForm;
